import React, {useEffect, useState} from 'react'
import {Field, FieldsManager} from '../../../data/services/fields'
import LocalStorage from '../../../util/localStorage'
import {useDispatch, useSelector} from 'react-redux'
import Resources from '../../../data/services/resources'
import {getSecondResource} from '../../../data/actions/secondResource'
import {download} from '../../../data/actions/download'
import {
    checkPerm,
    classNames,
    getDefaultTableOptions,
    getProp,
    saveTableColumns
} from '../../../common/util/util-helpers'
import {currentDate, toFrontDateNoTime} from '../../../common/util/util-dates'
import {READ_PERM} from '../../../common/util/util-consts'
import InventoryReportDialog from '../../../common/components/modal/inventory-report-dialog'
import moment from 'moment'
import Tippy from '@tippyjs/react'
import DocumentArrowDownIcon from '@heroicons/react/20/solid/DocumentArrowDownIcon'
import {ClockIcon, InformationCircleIcon} from "@heroicons/react/24/outline"
import TableCard from "../../../common/components/resource-table/table-components/table-card"
import NoRecordsTable from "../../../common/components/no-records-found/no-records-table"
import TableFilters from "../../../common/components/resource-table/table-components/table-filters"
import ActiveFilters from "../../../common/components/resource-table/table-components/active-filters"
import Badge from "../../../common/components/badge"
import ResourceTable from "../../../common/components/resource-table"
import {LoaderSmall} from "../../../common/components/loader"
import {numberWithCommas} from "../../../util/util-formaters"
import {HelpList, ReportHelpPanel, SIGNAL_LABELS, VALUATION_NOTE, helpFor, withSortDisabled} from "./inventory-exceptions-help";
import Pagination from "../../../common/components/resource-table/table-components/pagination";
import TableCardFooter from "../../../common/components/resource-table/table-components/table-card-footer";

/**
 * Parts that were bought recently and should have moved by now, but have not.
 *
 * The rule: nothing has left stock since the last purchase, and that purchase is older
 * than "Expected turn days" but newer than "Window days". Anything older than the window
 * is no longer something you can chase - it belongs on a clean-up report, not this one.
 *
 * End-user documentation lives in the backend repo at docs/INV_TRACKING.md.
 */

const CAUSE_LABELS = {
    UNBILLED_WORK_ORDER: 'Unbilled work order',
    OVERDUE_TURN: 'Overdue turn',
    RESTOCK_NOT_MOVING: 'Restock not moving'
}

const CAUSE_BADGE_TYPE = {
    UNBILLED_WORK_ORDER: 'red',
    OVERDUE_TURN: 'yellow',
    RESTOCK_NOT_MOVING: 'blue'
}

const SETTINGS_HELP = [
    ['Expected turn days', 'how long stock may sit after you buy it. Anything newer than this is not shown; it has not had time to move.'],
    ['Window days', 'how far back to look. Older than this is no longer something you can chase, and belongs on a clean-up report.']
]

const CAUSE_HELP = [
    ['Unbilled work order', 'it went on a truck and was never invoiced. Money you already spent and have not charged for. Chase the work order.'],
    ['Overdue turn', 'bought, never sold, still sitting. Wrong part, cancelled job, or a missed invoice line.'],
    ['Restock not moving', 'this part does sell, but the batch you last bought has not moved at all.']
]

export default function InventoryVelocityReportTab({
                                                       translate,
                                                       pagePath
                                                   }) {

    /** Store
     ================================================================= */
    const dispatch = useDispatch()
    const secondResource = useSelector((state) => state.secondResource)
    // The details dialog fetches its own data into dialogResource, and its invoice/expense
    // drill-down lands in thirdResource.
    const dialogResource = useSelector((state) => state.dialogResource)
    const thirdResource = useSelector((state) => state.thirdResource)
    const isLoading = secondResource.isLoading
    const data = secondResource?.data?.list ?? []
    const count = getProp(secondResource.data, 'count', 0)

    // Totals are whole-population: they cover every overdue part, not just this page, and
    // they do not narrow when the Cause filter does. That is deliberate - the breakdown
    // is what you filter *by*, so hiding it behind its own filter would be useless.
    const totals = secondResource?.data?.Totals ?? {}
    const totalValue = totals.Value ?? 0
    const byCause = totals.ByCause ?? {}

    /** Helpers
     ================================================================= */

    const getFields = () => {
        // Headers for columns the endpoint cannot sort by are made non-clickable.
        return withSortDisabled({
            LikelyCause: new Field('LikelyCause', '', [''], false, 'custom', {
                render: (item) => {
                    const cause = item.LikelyCause
                    if (!cause) return '-'
                    return (
                        <Badge type={CAUSE_BADGE_TYPE[cause] ?? 'outline'}>
                            {CAUSE_LABELS[cause] ?? cause}
                        </Badge>
                    )
                }
            }),
            ProductService: new Field('ProductService', '', [''], false, 'text', {}),
            SKU: new Field('SKU', '', [''], false, 'text', {}),
            OverdueDays: new Field('OverdueDays', '', [''], false, 'custom', {
                render: (item) => {
                    const days = item.OverdueDays
                    if (days === null || days === undefined) return '-'
                    // Past a month with nothing moving is a different kind of problem to
                    // a part that is a few days late, so the colour breaks at 30.
                    const colorClass = days >= 30 ? 'text-red-600' : days >= 7 ? 'text-yellow-600' : 'text-tm-gray-700'
                    return <span className={colorClass + ' font-semibold'}>{days} d</span>
                }
            }),
            OnHandQty: new Field('OnHandQty', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.OnHandQty ?? 0, 2)
            }),
            OnHandValue: new Field('OnHandValue', '', [''], false, 'decimal', {
                render: (item) => '$' + numberWithCommas(item.OnHandValue ?? 0, 2)
            }),
            UnitCost: new Field('UnitCost', '', [''], false, 'decimal', {
                render: (item) => item.UnitCost ? '$' + numberWithCommas(item.UnitCost, 2) : '-'
            }),
            LastPurchaseDate: new Field('LastPurchaseDate', '', [''], false, 'custom', {
                render: (item) => item.LastPurchaseDate ? toFrontDateNoTime(item.LastPurchaseDate) : '-'
            }),
            PurchaseEvents: new Field('PurchaseEvents', '', [''], false, 'custom', {
                render: (item) => item.PurchaseEvents ?? 0
            }),
            SellQty: new Field('SellQty', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.SellQty ?? 0, 2)
            }),
            LastSaleDate: new Field('LastSaleDate', '', [''], false, 'custom', {
                render: (item) => item.LastSaleDate ? toFrontDateNoTime(item.LastSaleDate) : 'Never'
            }),
            WorkOrderQty: new Field('WorkOrderQty', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.WorkOrderQty ?? 0, 2)
            }),
            WOQtyNeverBilled: new Field('WOQtyNeverBilled', '', [''], false, 'custom', {
                render: (item) => {
                    const qty = item.WOQtyNeverBilled ?? 0
                    if (!qty) return '-'
                    return <span className="text-red-600 font-semibold">{numberWithCommas(qty, 2)}</span>
                }
            }),
            DaysOfSupply: new Field('DaysOfSupply', '', [''], false, 'custom', {
                // Null means nothing has ever left, so there is no rate to project from.
                // That is a different statement to "a very large number of days".
                render: (item) => item.DaysOfSupply === null || item.DaysOfSupply === undefined
                    ? 'No sales'
                    : numberWithCommas(item.DaysOfSupply, 0)
            }),
            Signals: new Field('Signals', '', [''], false, 'custom', {
                // Plain text, not badges. A part can carry several signals, and wrapping
                // badges made the row grow to fit them, which broke the grid alignment.
                render: (item) => {
                    const signals = item.Signals ?? []
                    if (!signals.length) return '-'

                    const text = signals.map((s) => SIGNAL_LABELS[s] ?? s).join(', ')
                    return <span title={text}>{text}</span>
                }
            })
        })
    }

    const getQueryFields = () => {
        return {
            query: new Field('query', '', [''], false, 'search', {hideLabel: true, labelType: "float"}),
            // Labels resolve as field.<name> from src/assets/locale/en-wo.json, so no
            // explicit label metadata is needed.
            ExpectedTurnDays: new Field('ExpectedTurnDays', '14', [''], false, 'integer', {labelType: "float"}),
            WindowDays: new Field('WindowDays', '90', [''], false, 'integer', {labelType: "float"}),
            MinOnHandValue: new Field('MinOnHandValue', '0', [''], false, 'integer', {labelType: "float"}),
            // Multi: the causes are not mutually exclusive as a worklist. Seeing "unbilled
            // work order" and "overdue turn" together is the normal way to work this
            // report, and a single-value select could not be cleared back to "all" from
            // the control itself.
            LikelyCause: new Field('LikelyCause', '', [''], false, 'multi-select', {labelType: "float"}, {
                isMulti: true,
                all: true
            }),
            offset: new Field('offset', '0', [''], false, 'text'),
            limit: new Field('limit', '100', [''], false, 'select', {hideLabel: true, labelType: "float"}, {
                menuPlacement: 'top',
                hasPortal: true
            })
        }
    }

    const getResourceName = () => {
        return Resources.InventoryVelocity
    }

    const getQuery = () => {
        return {
            ...FieldsManager.getFieldKeyValues(queryFilterFields),
            Mode: 'velocity',
            sort: sort,
            sortBy: sortBy
        }
    }

    const getTableOptions = (pagePath, translate) => {
        const tablePageDefaults = {
            version: 1,
            behaviour: {
                rowSelect: false,
                canAdjustWidth: true,
                hasMenu: true
            },
            style: {
                condensed: true,
                isGPUAccelerated: true
            }
        }

        return getDefaultTableOptions(getFields(), tablePageDefaults, pagePath, translate)
    }

    /** State
     ================================================================= */
    const [queryFilterFields, setQueryFilterFields] = useState(getQueryFields())
    const [tableOptions, setTableOptions] = useState(getTableOptions(pagePath, translate))
    const [isTableFullScreen, setIsTableFullScreen] = useState(false)
    const [sort, setSort] = useState("DESC")
    const [sortBy, setSortBy] = useState("OverdueDays")
    const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)
    const [selectedReportItem, setSelectedReportItem] = useState({})
    const [isHelpOpen, setIsHelpOpen] = useState(false)

    // Opening the movement ledger needs the parts report permission, which is a separate
    // grant to this report. Without it the dialog would open on an empty response, so the
    // row is simply not clickable.
    const canViewDetails = checkPerm(Resources.WorkOrderProductServicesReport, READ_PERM)

    // TableFilters resolves a select's options as selects[fieldName][value] -> label,
    // which is exactly the shape CAUSE_LABELS already has.
    const metadata = {
        LikelyCause: CAUSE_LABELS
    }

    /** Data events
     ================================================================= */
    const fetchData = () => {
        dispatch(getSecondResource({
            user: LocalStorage.get('user'),
            resource: getResourceName(),
            query: getQuery()
        }))
    }

    // Exports the filtered report rather than the page on screen: the server replaces
    // paging with its own export cap when format=EXCEL.
    const downloadExcel = () => {
        dispatch(download({
            user: LocalStorage.get('user'),
            resource: getResourceName(),
            query: Object.assign({
                format: 'EXCEL',
                name: 'inventory_velocity_' + currentDate() + '.xlsx'
            }, getQuery())
        }))
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        fetchData()
    }, [sort, sortBy, queryFilterFields])

    /** UI events
     ================================================================= */
    const handleFilterInputChange = (name, value) => {
        let queryFieldsUpdate = FieldsManager.updateField(queryFilterFields, name, value)

        // Any filter change invalidates the page you were on.
        if (name !== 'offset') {
            queryFieldsUpdate = FieldsManager.updateField(queryFieldsUpdate, 'offset', '0')
        }

        setQueryFilterFields(queryFieldsUpdate)
    }

    const handleClearFiltersClick = (excludeAdditional = []) => {
        const queryFilterFieldsTmp = Object.assign({}, queryFilterFields)
        // Clearing the thresholds to empty would silently fall back to the server defaults
        // and leave the inputs looking blank, so they are restored to their defaults.
        const defaultExcludedFields = ['limit', 'ExpectedTurnDays', 'WindowDays', 'MinOnHandValue']
        const excludedFields = defaultExcludedFields.concat(excludeAdditional)

        Object.values(queryFilterFieldsTmp).filter((it) => !excludedFields.includes(it.name)).forEach((it) => {
            FieldsManager.updateField(queryFilterFieldsTmp, it.name, '')
        })

        FieldsManager.updateField(queryFilterFieldsTmp, 'ExpectedTurnDays', '14')
        FieldsManager.updateField(queryFilterFieldsTmp, 'WindowDays', '90')
        FieldsManager.updateField(queryFilterFieldsTmp, 'MinOnHandValue', '0')

        setQueryFilterFields(queryFilterFieldsTmp)
    }

    const handleFullScreenToggle = () => {
        setIsTableFullScreen(!isTableFullScreen)
    }

    const setOptions = (options) => {
        setTableOptions(options)
        saveTableColumns(pagePath, options)
    }

    const handleUpdateSort = (sortByTmp) => {
        setSort((sortByTmp === sortBy) ? (sort === "ASC" ? "DESC" : "ASC") : "DESC")
        setSortBy(sortByTmp)
    }

    const handleRowClick = (item) => {
        if (!canViewDetails || !item?.ProductServiceID) return

        setSelectedReportItem(item)
        setIsReportDialogOpen(true)
    }

    /** Render
     ================================================================= */
    return (
        <React.Fragment>
            <div className={"flex items-center mb-4"}>
                <div className="flex mx-4 gap-4 flex-wrap">
                    <Badge addClass={"relative"} type="outline">
                        {isLoading && (
                            <div className="px-5">
                                <LoaderSmall/>
                            </div>
                        )}

                        {!isLoading && (
                            <Tippy content={
                                // Deliberately short. The full glossary lives behind the
                                // info icon next to these badges; a hover card that needs
                                // its own scrollbar is not a hover card.
                                <div className="text-xs p-1 max-w-sm">
                                    <p className="mb-2">Parts you bought recently that should have gone out by now.
                                        A part appears here when <span className="font-medium">nothing has left stock since you last bought it</span>,
                                        and that purchase is older than your expected turn time.</p>

                                    <p className="font-semibold mb-1">The two settings</p>
                                    <HelpList items={SETTINGS_HELP} addClass="mb-2"/>

                                    <p className="font-semibold mb-1">What the causes mean</p>
                                    <HelpList items={CAUSE_HELP}/>

                                    <p className="mt-2">{VALUATION_NOTE}</p>
                                </div>
                            } allowHTML={true} maxWidth={400}>
                                <div className="flex items-center gap-1.5">
                                    <ClockIcon className="w-5 h-5"/>
                                    <span>Overdue parts:</span>
                                    <span className="font-bold">{totals.Products ?? 0}</span>
                                </div>
                            </Tippy>
                        )}
                    </Badge>

                    {!isLoading && !!(totals.Products ?? 0) && (
                        <>
                            <Badge type="outline">
                                <span>Value sitting: </span>
                                <span className="font-bold">${numberWithCommas(totalValue, 2)}</span>
                            </Badge>

                            {Object.keys(CAUSE_LABELS)
                                .filter((cause) => !!byCause[cause])
                                .map((cause) => (
                                    <Badge key={cause} type={CAUSE_BADGE_TYPE[cause]}>
                                        <span>{CAUSE_LABELS[cause]}: </span>
                                        <span className="font-bold">{byCause[cause].Products}</span>
                                        <span className="ml-1">(${numberWithCommas(byCause[cause].Value, 2)})</span>
                                    </Badge>
                                ))}
                        </>
                    )}

                    <Tippy content={isHelpOpen ? 'Hide explanation' : 'How this report works'}>
                        <button
                            className="btn-icon"
                            aria-expanded={isHelpOpen}
                            onClick={() => setIsHelpOpen(!isHelpOpen)}
                        >
                            <InformationCircleIcon className={
                                classNames("w-5 h-5", isHelpOpen ? "text-primary" : "text-tm-gray-500")
                            }/>
                        </button>
                    </Tippy>
                </div>
            </div>

            {isHelpOpen && (
                <ReportHelpPanel
                    title="How this report works"
                    intro={(
                        <>Parts you bought recently that should have gone out by now. A part appears here
                            when <span className="font-medium">nothing has left stock since you last bought
                            it</span>, and that purchase is older than your expected turn time.</>
                    )}
                    settings={SETTINGS_HELP}
                    causes={CAUSE_HELP}
                    // Derived from the grid's own column list, so the glossary cannot list a
                    // column the table does not show, or miss one it does.
                    columns={helpFor(Object.keys(getFields()))}
                    onClose={() => setIsHelpOpen(false)}
                />
            )}

            <div className={"sm:flex justify-between"}>
                <ActiveFilters
                    addClass={"mb-2"}
                    filterFields={queryFilterFields}
                    onLabelClick={handleFilterInputChange}
                    persistFilters={[]}
                    onClearFiltersClick={handleClearFiltersClick}
                    translate={translate}
                />

                <div>
                    <Tippy content={translate('text.download_excel')}>
                        <button className="btn-icon" onClick={downloadExcel}>
                            <DocumentArrowDownIcon className="w-6 h-6"/>
                        </button>
                    </Tippy>
                </div>
            </div>

            <TableCard
                className={
                    classNames(
                        "",
                        isTableFullScreen
                            ? "bg-inverse rounded-card fixed inset-1 -top-1 z-50"
                            : "flex flex-col h-dialog-body md:bg-inverse md:border border-tm-gray-300 md:shadow-card"
                    )}
            >
                <div>
                    <TableFilters
                        forceDialog
                        hideLimit
                        filterFields={queryFilterFields}
                        handleInputChange={handleFilterInputChange}
                        translate={translate}
                        onRefreshTable={fetchData}
                        selects={metadata}
                        onFullScreenToggle={handleFullScreenToggle}
                        isTableFullScreen={isTableFullScreen}
                        isLoading={isLoading}
                    />
                </div>

                <ResourceTable
                    tableKey={'ProductServiceID'}
                    data={data}
                    count={count}
                    fields={getFields()}
                    options={tableOptions}
                    isLoading={isLoading}
                    translate={translate}
                    sort={sort}
                    sortBy={sortBy}
                    onSortChange={handleUpdateSort}
                    saveTableOptions={setOptions}
                    maxHeightClass={"max-h-[calc(100vh-28rem)]"}
                    onRowClick={canViewDetails ? handleRowClick : undefined}
                />

                <NoRecordsTable
                    show={(data.length === 0) && !isLoading}
                    title={'Nothing is overdue'}
                    text={'Every part bought in the last ' + (queryFilterFields.WindowDays?.value ?? 90) + ' days has moved within ' + (queryFilterFields.ExpectedTurnDays?.value ?? 14) + ' days.'}
                    className={"pb-12 pt-16 px-6"}
                />

                <TableCardFooter
                    show={!(!data.length && !isLoading)}
                >
                    <Pagination
                        count={count}
                        isLoading={isLoading}
                        handleQueryChange={handleFilterInputChange}
                        queryFields={queryFilterFields}
                        translate={translate}
                    />
                </TableCardFooter>
            </TableCard>

            <InventoryReportDialog
                isReportDialogOpen={isReportDialogOpen}
                setIsReportDialogOpen={setIsReportDialogOpen}
                selectedReportItem={selectedReportItem}
                dialogResource={dialogResource}
                thirdResource={thirdResource}
                resource={Resources.WorkOrderProductServicesReport}
                dispatch={dispatch}
                translate={translate}
                // A rolling year, not the calendar year the dialog otherwise defaults to.
                // The purchase that put a part on this report can sit in the previous year
                // whenever the window reaches back across January, and a year of history
                // is also what tells you whether a stalled restock is out of character.
                startDate={moment().subtract(1, 'year').format('YYYY-MM-DD')}
                endDate={moment().format('YYYY-MM-DD')}
            />
        </React.Fragment>
    )
}
